/**
 * Evidence Summary Tab
 *
 * Shown to ALL disclosure levels (MEMBER, LENDER, AUDITOR).
 * Displays: value, period, trend, coaching tip.
 *
 * For LENDER+ levels, also shows Run Metadata (Audit Q2.1, Q2.3):
 * - Algorithm version / git SHA
 * - Data coverage (transactions, accounts, months)
 * - Run status
 *
 * BUG-UI-061: For gross_income_6m, shows both attested and deposited income
 * BUG-UI-036: Uses selectedMonth for period/as_of display (not record's month_key)
 */

import React from 'react';
import { TrendingUp, TrendingDown, Minus, Calendar, Clock, Database, GitBranch, CheckCircle2, AlertCircle, AlertTriangle, Info, HelpCircle, Shield, FileCheck } from 'lucide-react';
import { EvidenceContract } from '@/types/evidenceContract';
import { EvidenceRunMetadata } from '@/services/auditEvidenceService';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/Tooltip';
import { getInterpretation as getMetricInterpretation, getBandingDisplay as getMetricBandingDisplay } from '@/config/metricBanding';
import { isDemo2Member } from '@/services/demo2PersonaOverlay';

/** BUG-UI-061: Income basis data type for gross_income_6m comparison */
interface IncomeBasisData {
  income_basis_used: 'ATTESTED' | 'DEPOSITED' | 'VERIFIED';
  income_confidence: number;
  income_sources: {
    attested?: number;
    deposited_net?: number;
    verified_gross?: number;
  };
  selection_reason?: string;
}

/** EPIC 62 Phase 2B: FactorAuditDTO confidence structure */
interface FactorAuditDTOConfidence {
  confidence?: {
    score: number;
    tier: 'HIGH' | 'MEDIUM' | 'LOW';
    drivers?: Array<{
      name: string;
      impact: number;
      description: string;
    }>;
  };
  quality_rollup?: {
    accounts_used: number;
    txns_used: number;
    coverage_months: number;
    freshness_days: number;
  };
  algorithm_metadata?: {
    version: string;
    git_sha: string;
    ruleset_version: string;
  };
}

/** BUG-UI-120: Dashboard value data for reconciliation */
interface DashboardValueData {
  value: number;
  formattedValue: string;
  month?: string;
  unit?: 'USD' | 'PERCENT' | 'COUNT';
}

interface EvidenceSummaryTabProps {
  evidence: EvidenceContract | null;
  /** BUG-UI-036: Selected month from dropdown to ensure date alignment */
  selectedMonth?: string | null;
  /** BUG-UI-061: Income basis data for gross_income_6m comparison */
  incomeBasis?: IncomeBasisData;
  /** BUG-EPIC61-009: Run-level metadata for Trust Panel */
  runMetadata?: EvidenceRunMetadata;
  /** BUG-UI-120: Dashboard value for reconciliation with modal display */
  dashboardValue?: DashboardValueData;
  /** Phase 16 (Demo2): suppress BUG-UI-036 Date Notice for Demo2 personas. */
  memberId?: string;
}

export function EvidenceSummaryTab({ evidence, selectedMonth, incomeBasis: incomeBasisProp, runMetadata, dashboardValue, memberId }: EvidenceSummaryTabProps) {
  if (!evidence) {
    return (
      <div className="text-center text-muted-foreground py-8">
        No evidence data available
      </div>
    );
  }

  const { summary, value, unit, period, as_of, metric_key } = evidence;

  // BUG-UI-061: Extract income_basis from evidence.intermediate_metrics if not passed as prop
  // This allows the component to work both with explicit prop and with API data
  const incomeBasis: IncomeBasisData | undefined = incomeBasisProp ||
    (evidence?.intermediate_metrics?.income_basis as IncomeBasisData | undefined);

  // BUG-UI-035, BUG-UI-037: Format value based on unit, with override for factor scores
  // Factor scores and VeraScore are 0-100 scale scores, NOT percentages
  const effectiveUnit = getEffectiveUnit(metric_key, unit);
  const formattedValue = formatValue(value, effectiveUnit);

  // Trend icon and color
  const TrendIcon = getTrendIcon(summary.trend);
  const trendColor = getTrendColor(summary.trend);

  // BUG-UI-036 FIX: Always show actual evidence date, not selected month
  // The evidence.period reflects what data was actually used, so display that
  const actualEvidenceMonth = formatAsOfDate(as_of, evidence.period?.start);
  const displayPeriod = formatPeriod(period);
  const displayAsOf = actualEvidenceMonth;

  // BUG-UI-036 FIX: Detect mismatch between selected month and actual evidence month
  // Compare YYYY-MM strings directly to avoid timezone/formatting inconsistencies
  const actualMonthKey = extractMonthKey(as_of, evidence.period?.start);
  const selectedMonthFormatted = selectedMonth ? formatMonthFromKey(selectedMonth) : null;
  const hasDateMismatch = selectedMonth && actualMonthKey &&
    !selectedMonth.startsWith(actualMonthKey) && actualMonthKey !== selectedMonth.substring(0, 7);

  // BUG-UI-061: Check if this is gross_income_6m and we have income basis data to show comparison
  const isIncomeMetric = metric_key === 'gross_income_6m';
  const hasIncomeMismatch = isIncomeMetric && incomeBasis &&
    incomeBasis.income_sources.attested &&
    incomeBasis.income_sources.deposited_net &&
    Math.abs(incomeBasis.income_sources.attested - incomeBasis.income_sources.deposited_net) > 1000;

  // BUG-UI-120 FIX: Detect data mismatch between dashboard and evidence
  // We show evidence data in all tabs for consistency, but warn when dashboard differs significantly
  const dashboardMonthKey = dashboardValue?.month
    ? (() => {
        // Parse "Nov 2025" or "Oct 2025" format to YYYY-MM
        const parts = dashboardValue.month.split(' ');
        if (parts.length === 2) {
          const monthMap: Record<string, string> = {
            'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
            'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
            'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
          };
          const month = monthMap[parts[0]];
          const year = parts[1];
          return month && year ? `${year}-${month}` : null;
        }
        return null;
      })()
    : null;

  // Check if we should compare dashboard vs evidence values
  // Case 1: No month tracking on dashboard (dashboardValue.month undefined) - show on initial load
  // Case 2: Month is tracked - show when viewing same month as dashboard
  const shouldCompareValues = dashboardValue && (
    !selectedMonth || // Initial load - always compare
    !dashboardValue.month || // No month tracking on dashboard - always compare on initial load
    (dashboardMonthKey && selectedMonth.startsWith(dashboardMonthKey)) // Month tracked and matches
  );

  // Detect significant mismatch between dashboard and evidence values
  const hasDashboardMismatch = shouldCompareValues && dashboardValue &&
    Math.abs(dashboardValue.value - value) > 1; // More than $1 or 1% difference

  return (
    <div className="space-y-4">
      {/* BUG-UI-120: Show warning when dashboard value differs from evidence */}
      {hasDashboardMismatch && (
        <div className="bg-amber-900/30 border border-amber-600 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <span className="text-amber-400 text-lg">⚠️</span>
            <div className="text-sm">
              <div className="text-amber-300 font-medium">Data Source Difference</div>
              <div className="text-amber-200/80 mt-1">
                Dashboard shows <strong>{dashboardValue.formattedValue}</strong> but evidence data shows <strong>{formattedValue}</strong>.
                The dashboard uses aggregated statistics while this modal shows detailed evidence records.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EPIC 61: 3-BLOCK TEMPLATE */}

      {/* BLOCK A: PRIMARY KPI */}
      {/* BUG-UI-120: Always show evidence value for consistency across tabs */}
      <PrimaryKPIBlock
        value={value}
        formattedValue={formattedValue}
        unit={effectiveUnit}
        metricKey={metric_key}
        trend={summary.trend}
        trendPercent={summary.trend_percent}
        isIncomeMetric={isIncomeMetric}
        asOfDate={displayAsOf}
      />

      {/* BLOCK B: WHAT WAS USED */}
      <WhatWasUsedBlock
        evidence={evidence}
        metricKey={metric_key}
        incomeBasis={incomeBasis}
      />

      {/* BLOCK C: TRUST PANEL */}
      {/* BUG-EPIC61-009: Use runMetadata from API response instead of evidence.run_metadata */}
      {/* EPIC 62 Phase 2B: Pass factor_audit_dto for enhanced confidence display */}
      {runMetadata ? (
        <TrustPanelBlock
          metadata={runMetadata}
          metricKey={metric_key}
          dataFreshnessTs={displayAsOf}
          factorAuditDTO={evidence?.intermediate_metrics?.factor_audit_dto as FactorAuditDTOConfidence | undefined}
        />
      ) : (
        <TrustPanelFallback
          metricKey={metric_key}
          dataFreshnessTs={displayAsOf}
          factorAuditDTO={evidence?.intermediate_metrics?.factor_audit_dto as FactorAuditDTOConfidence | undefined}
        />
      )}

      {/* BUG-UI-036: Date mismatch warning.
          Phase 16 (Demo2): suppressed for Demo2 personas. The Phase 14
          frontend date-window shift relabels evidence months to align with
          the current Demo2 score_month, so the BUG-UI-036 banner would always
          fire and expose an implementation detail that is irrelevant in the
          demo context. Behavior for non-Demo orgs is unchanged. */}
      {hasDateMismatch && !isDemo2Member(memberId) && (
        <div className="p-3 bg-amber-900/30 border border-amber-600 rounded-lg text-amber-300 text-sm">
          <span className="font-medium">⚠️ Date Notice:</span> Selected "{selectedMonthFormatted}" but showing data from "{actualEvidenceMonth}" (most recent available).
        </div>
      )}

      {/* Coaching Tip (FCRA reason code explanation) */}
      {summary.coaching_tip && (
        <div className="p-4 border-l-4 border-amber-400 bg-amber-900/30">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-amber-300 border-amber-500">
              Coaching Tip
            </Badge>
          </div>
          <p className="text-sm text-amber-200">
            {summary.coaching_tip}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * EPIC 61 - BLOCK A: PRIMARY KPI
 * Shows the main metric value, score, interpretation, and banding
 * BUG-UI-120: Always show evidence value for consistency across all tabs
 */
function PrimaryKPIBlock({
  value,
  formattedValue,
  unit,
  metricKey,
  trend,
  trendPercent,
  isIncomeMetric,
  asOfDate,
}: {
  value: number;
  formattedValue: string;
  unit: string;
  metricKey: string;
  trend?: 'UP' | 'DOWN' | 'STABLE';
  trendPercent?: number;
  isIncomeMetric: boolean;
  asOfDate?: string;
}) {
  const TrendIcon = getTrendIcon(trend);
  const trendColor = getTrendColor(trend);

  // US61.2: Add banding and interpretation logic
  // BUG-EPIC61-004: Pass value to getBanding to show specific range
  const interpretation = getInterpretation(metricKey, value);
  const banding = getBanding(metricKey, value);

  return (
    <div className="border border-slate-600 rounded-lg overflow-hidden">
      <div className="bg-slate-700 px-4 py-2">
        <span className="text-sm font-medium text-white">Primary KPI</span>
      </div>
      <div className="p-4 bg-slate-800 space-y-3">
        {/* Main Value */}
        <div className="text-center">
          <div className="text-4xl font-bold text-white">
            {formattedValue}
          </div>
          {isIncomeMetric && (
            <div className="text-sm text-slate-400 mt-1">
              Based on Deposited Income
            </div>
          )}
          {/* BUG-EPIC61-005: Show as-of date */}
          {asOfDate && (
            <div className="text-xs text-slate-500 mt-1">
              As of: {asOfDate}
            </div>
          )}
        </div>

        {/* Trend - BUG-EPIC61-003: Enhanced with label */}
        {trend && (
          <div className={`flex items-center justify-center gap-2 ${trendColor}`}>
            <TrendIcon className="w-4 h-4" />
            <span className="text-sm font-medium">
              {getTrendLabel(trend)}
              {trendPercent !== undefined && ` (${trendPercent > 0 ? '+' : ''}${trendPercent.toFixed(1)}%)`}
            </span>
          </div>
        )}

        {/* Interpretation Badge */}
        {interpretation && (
          <div className="flex items-center justify-center">
            <Badge
              variant="outline"
              className={`${interpretation.color} border-current`}
            >
              {interpretation.icon} {interpretation.label}
            </Badge>
          </div>
        )}

        {/* Banding Display */}
        {banding && (
          <div className="text-xs text-slate-400 text-center pt-2 border-t border-slate-600">
            {banding}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * EPIC 61 - BLOCK B: WHAT WAS USED
 * Shows numerator/denominator breakdown with notes
 */
function WhatWasUsedBlock({
  evidence,
  metricKey,
  incomeBasis,
}: {
  evidence: EvidenceContract;
  metricKey: string;
  incomeBasis?: IncomeBasisData;
}) {
  // Get metric-specific "what was used" content
  const whatWasUsed = getWhatWasUsedContent(evidence, metricKey, incomeBasis);

  if (!whatWasUsed) return null;

  return (
    <div className="border border-slate-600 rounded-lg overflow-hidden">
      <div className="bg-slate-700 px-4 py-2">
        <span className="text-sm font-medium text-white">What Was Used</span>
      </div>
      <div className="p-4 bg-slate-800 space-y-2">
        {whatWasUsed.map((item, index) => (
          <div key={index} className="text-sm">
            <span className="text-white font-medium">{item.label}:</span>{' '}
            <span className="text-slate-300">{item.value}</span>
            {item.note && (
              <div className="text-xs text-slate-400 mt-1 ml-4">
                {item.note}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * EPIC 61 - BLOCK C: TRUST PANEL
 * Shows coverage, freshness, and confidence (Epic 65M)
 * BUG-EPIC61-009: Updated to use EvidenceRunMetadata from API response
 * EPIC 62 Phase 2B: Enhanced to use factor_audit_dto confidence when available
 */
function TrustPanelBlock({
  metadata,
  metricKey,
  dataFreshnessTs,
  factorAuditDTO,
}: {
  metadata: EvidenceRunMetadata;
  metricKey: string;
  dataFreshnessTs: string;
  factorAuditDTO?: FactorAuditDTOConfidence;
}) {
  // EPIC 62 Phase 2B: Prefer factor_audit_dto confidence when available (single source of truth)
  const dtoConfidence = factorAuditDTO?.confidence;
  const effectiveConfidenceScore = dtoConfidence?.score ?? metadata.confidence_score;
  const effectiveConfidenceTier = dtoConfidence?.tier ?? calculateConfidenceTier(metadata.confidence_score);

  // Calculate confidence display values
  const confidenceTier = effectiveConfidenceTier;
  const confidenceColor = getConfidenceColor(confidenceTier);
  const confidenceIcon = getConfidenceIcon(confidenceTier);

  // Format coverage display
  const coverageDisplay = formatCoverageDisplay(metadata);

  return (
    <div className="border border-slate-600 rounded-lg overflow-hidden">
      <div className="bg-slate-700 px-4 py-2">
        <span className="text-sm font-medium text-white">Trust Panel</span>
      </div>
      <div className="p-4 bg-slate-800 space-y-3">
        {/* Coverage */}
        <div className="flex items-start gap-2">
          <Database className="w-4 h-4 text-slate-400 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm text-white font-medium">Coverage</div>
            <div className="text-sm text-slate-300">{coverageDisplay}</div>
          </div>
        </div>

        {/* Freshness */}
        <div className="flex items-start gap-2">
          <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm text-white font-medium">Freshness</div>
            <div className="text-sm text-slate-300">
              Data as of {dataFreshnessTs}
            </div>
          </div>
        </div>

        {/* Confidence (Epic 65M) - US61.3: Enhanced with detailed tooltip */}
        <div className="flex items-start gap-2">
          {confidenceIcon}
          <div className="flex-1">
            <div className="flex items-center gap-1">
              <div className="text-sm text-white font-medium">Confidence</div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="text-slate-400 hover:text-slate-300 transition-colors">
                      <HelpCircle className="w-3.5 h-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-sm bg-slate-900 border-slate-700">
                    {/* BUG-UI-108 FIX: Pass effectiveConfidenceScore to ensure tooltip matches panel */}
                    <ConfidenceBreakdownTooltip metadata={metadata} effectiveScore={effectiveConfidenceScore} />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Confidence tier and score */}
            <div className={`text-sm font-semibold ${confidenceColor}`}>
              {confidenceTier}
              {effectiveConfidenceScore !== undefined && (
                <span className="text-slate-400 font-normal ml-1">
                  ({effectiveConfidenceScore}/100)
                </span>
              )}
            </div>

            {/* Visual confidence meter - US61.3 */}
            {effectiveConfidenceScore !== undefined && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 bg-slate-600 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      effectiveConfidenceScore >= 70 ? 'bg-green-500' :
                      effectiveConfidenceScore >= 50 ? 'bg-amber-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${effectiveConfidenceScore}%` }}
                  />
                </div>
              </div>
            )}

            {/* EPIC 62 Phase 2B: Confidence drivers from factor_audit_dto */}
            {dtoConfidence?.drivers && dtoConfidence.drivers.length > 0 && (
              <div className="text-xs text-slate-400 mt-1 space-y-0.5">
                {dtoConfidence.drivers.slice(0, 2).map((driver, idx) => (
                  <div key={idx} className="flex items-center gap-1">
                    <span className={driver.impact >= 0 ? 'text-green-400' : 'text-amber-400'}>
                      {driver.impact >= 0 ? '+' : ''}{driver.impact}
                    </span>
                    <span>{driver.description}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Fallback: Confidence reasons from run metadata */}
            {!dtoConfidence?.drivers && metadata.confidence_reasons && metadata.confidence_reasons.length > 0 && (
              <div className="text-xs text-slate-400 mt-1">
                {formatConfidenceReasons(metadata.confidence_reasons)}
              </div>
            )}
          </div>
        </div>

        {/* Basis information (Epic 65M) */}
        {(metadata.income_basis_used || metadata.debt_basis_used) && (
          <div className="pt-2 border-t border-slate-600 text-xs text-slate-400 space-y-1">
            {metadata.income_basis_used && (
              <div>
                <span className="text-slate-500">Income basis:</span>{' '}
                <span className="text-slate-300">{formatBasisLabel(metadata.income_basis_used)}</span>
              </div>
            )}
            {metadata.debt_basis_used && (
              <div>
                <span className="text-slate-500">Debt basis:</span>{' '}
                <span className="text-slate-300">{formatBasisLabel(metadata.debt_basis_used)}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * BUG-EPIC61-001: Trust Panel Fallback
 * Shows when run_metadata is not available (graceful degradation)
 * EPIC 62 Phase 2B: Can still show factor_audit_dto confidence when available
 */
function TrustPanelFallback({
  metricKey,
  dataFreshnessTs,
  factorAuditDTO,
}: {
  metricKey: string;
  dataFreshnessTs: string;
  factorAuditDTO?: FactorAuditDTOConfidence;
}) {
  // EPIC 62 Phase 2B: Use factor_audit_dto confidence if available
  const dtoConfidence = factorAuditDTO?.confidence;
  const dtoQuality = factorAuditDTO?.quality_rollup;

  // If we have factor_audit_dto data, show a richer fallback
  if (dtoConfidence || dtoQuality) {
    const confidenceColor = dtoConfidence?.tier === 'HIGH' ? 'text-green-400' :
                           dtoConfidence?.tier === 'MEDIUM' ? 'text-amber-400' : 'text-red-400';

    return (
      <div className="border border-slate-600 rounded-lg overflow-hidden">
        <div className="bg-slate-700 px-4 py-2">
          <span className="text-sm font-medium text-white">Trust Panel</span>
        </div>
        <div className="p-4 bg-slate-800 space-y-3">
          {/* Coverage from quality_rollup */}
          {dtoQuality && (
            <div className="flex items-start gap-2">
              <Database className="w-4 h-4 text-slate-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-white font-medium">Coverage</div>
                <div className="text-sm text-slate-300">
                  {dtoQuality.coverage_months} months, {dtoQuality.accounts_used} accounts, {dtoQuality.txns_used} transactions
                </div>
              </div>
            </div>
          )}

          {/* Freshness */}
          <div className="flex items-start gap-2">
            <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm text-white font-medium">Freshness</div>
              <div className="text-sm text-slate-300">
                {dtoQuality?.freshness_days !== undefined ? `${dtoQuality.freshness_days} days old` : `Data as of ${dataFreshnessTs}`}
              </div>
            </div>
          </div>

          {/* Confidence from factor_audit_dto */}
          {dtoConfidence && (
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-slate-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-white font-medium">Confidence</div>
                <div className={`text-sm font-semibold ${confidenceColor}`}>
                  {dtoConfidence.tier}
                  <span className="text-slate-400 font-normal ml-1">({dtoConfidence.score}/100)</span>
                </div>
                {dtoConfidence.drivers && dtoConfidence.drivers.length > 0 && (
                  <div className="text-xs text-slate-400 mt-1 space-y-0.5">
                    {dtoConfidence.drivers.slice(0, 2).map((driver, idx) => (
                      <div key={idx} className="flex items-center gap-1">
                        <span className={driver.impact >= 0 ? 'text-green-400' : 'text-amber-400'}>
                          {driver.impact >= 0 ? '+' : ''}{driver.impact}
                        </span>
                        <span>{driver.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Original fallback when no data available
  return (
    <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
      <div className="flex items-start gap-3 mb-3">
        <Info className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-medium text-slate-200 mb-1">Trust & Audit Information</h3>
          <p className="text-xs text-slate-400">
            Detailed trust metrics are not yet available for this calculation run.
          </p>
        </div>
      </div>

      <div className="space-y-2 text-xs">
        {/* Data Freshness - Always available */}
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-500" />
          <span className="text-slate-400">Data freshness:</span>
          <span className="text-slate-200">{dataFreshnessTs}</span>
        </div>

        {/* Coverage - Placeholder */}
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-slate-500" />
          <span className="text-slate-400">Coverage:</span>
          <span className="text-slate-500 italic">Not available</span>
        </div>

        {/* Confidence - Placeholder */}
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-slate-500" />
          <span className="text-slate-400">Confidence:</span>
          <span className="text-slate-500 italic">Not available</span>
        </div>

        {/* Audit Status - Placeholder */}
        <div className="flex items-center gap-2">
          <FileCheck className="w-4 h-4 text-slate-500" />
          <span className="text-slate-400">Audit:</span>
          <span className="text-slate-500 italic">Integrity hash not yet recorded</span>
        </div>
      </div>

      {/* Call to Action */}
      <div className="mt-3 pt-3 border-t border-slate-700">
        <p className="text-xs text-slate-500">
          💡 Trust metrics will be available after the next metric recalculation (Epic 65M).
        </p>
      </div>
    </div>
  );
}

/**
 * US61.3: Confidence Breakdown Tooltip
 * Shows detailed breakdown of confidence score calculation
 * BUG-UI-108 FIX: Now accepts effectiveScore to ensure tooltip matches panel display
 * BUG-UI-111 FIX: Always show calculation breakdown even when reasons array is empty
 */
function ConfidenceBreakdownTooltip({ metadata, effectiveScore }: {
  metadata: NonNullable<EvidenceContract['run_metadata']>;
  effectiveScore?: number;
}) {
  // BUG-UI-108 FIX: Use effectiveScore (from factorAuditDTO or metadata) to match panel display
  const score = effectiveScore ?? metadata.confidence_score ?? 0;
  const reasons = metadata.confidence_reasons || [];

  // Parse reasons to show contributing factors
  const hasHighCoverage = reasons.includes('HIGH_COVERAGE');
  const hasPartialCoverage = reasons.includes('PARTIAL_COVERAGE');
  const hasLowCoverage = reasons.includes('LOW_COVERAGE');
  const hasVerifiedIncome = reasons.includes('VERIFIED_INCOME');
  const hasDepositedIncome = reasons.includes('DEPOSITED_INCOME');
  const hasAttestedIncome = reasons.includes('ATTESTED_INCOME');
  const hasCRS = reasons.includes('CRS_AVAILABLE');
  const noCRS = reasons.includes('CRS_UNAVAILABLE');
  const hasDataGaps = reasons.includes('DATA_GAPS_DETECTED');
  const hasLowQuality = reasons.includes('LOW_QUALITY');

  // BUG-UI-111: Determine if we have ANY reason data to show
  const hasAnyReasonData = reasons.length > 0;

  // Infer coverage from metadata if reasons not available
  const coverageMonths = metadata.coverage_months_used || metadata.coverage_months_available || 0;
  const inferredHighCoverage = coverageMonths >= 6;
  const inferredPartialCoverage = coverageMonths >= 3 && coverageMonths < 6;
  const inferredLowCoverage = coverageMonths > 0 && coverageMonths < 3;

  // Infer income basis from metadata
  const incomeBasis = metadata.income_basis_used;
  const inferredVerified = incomeBasis === 'VERIFIED_GROSS';
  const inferredDeposited = incomeBasis === 'DEPOSITED_NET';
  const inferredAttested = incomeBasis === 'ATTESTED' || (!incomeBasis && !inferredVerified && !inferredDeposited);

  // Infer CRS from metadata
  const debtBasis = metadata.debt_basis_used;
  const inferredCRS = debtBasis === 'CRS_BUREAU';

  return (
    <div className="space-y-3 p-2">
      <div>
        <div className="text-sm font-semibold text-white mb-1">Confidence Score Breakdown</div>
        <div className="text-xs text-slate-300">
          Run-level score reflecting data quality and completeness. All metrics from this run share the same confidence.
        </div>
      </div>

      {/* Score calculation breakdown - ALWAYS show */}
      <div className="space-y-1.5">
        <div className="text-xs font-medium text-slate-200">How it's calculated:</div>

        {/* Base score - always show */}
        <div className="flex items-start gap-2 text-xs">
          <span className="text-slate-400 w-8 text-right">50</span>
          <span className="text-slate-300">Base score</span>
        </div>

        {/* Coverage - show from reasons OR inferred */}
        {(hasHighCoverage || hasPartialCoverage || hasLowCoverage) ? (
          <div className="flex items-start gap-2 text-xs">
            <span className={`w-8 text-right ${hasHighCoverage ? 'text-green-400' : hasPartialCoverage ? 'text-amber-400' : 'text-red-400'}`}>
              {hasHighCoverage ? '+20' : hasPartialCoverage ? '+10' : '-10'}
            </span>
            <span className="text-slate-300">
              {hasHighCoverage ? '6+ months coverage' : hasPartialCoverage ? '3-5 months coverage' : '<3 months coverage'}
            </span>
          </div>
        ) : (inferredHighCoverage || inferredPartialCoverage || inferredLowCoverage) ? (
          <div className="flex items-start gap-2 text-xs">
            <span className={`w-8 text-right ${inferredHighCoverage ? 'text-green-400' : inferredPartialCoverage ? 'text-amber-400' : 'text-red-400'}`}>
              {inferredHighCoverage ? '+20' : inferredPartialCoverage ? '+10' : '-10'}
            </span>
            <span className="text-slate-300">
              {coverageMonths} months coverage
            </span>
          </div>
        ) : (
          <div className="flex items-start gap-2 text-xs">
            <span className="w-8 text-right text-slate-500">—</span>
            <span className="text-slate-400">Coverage data unavailable</span>
          </div>
        )}

        {/* Income basis - show from reasons OR inferred */}
        {(hasVerifiedIncome || hasDepositedIncome || hasAttestedIncome) ? (
          <div className="flex items-start gap-2 text-xs">
            <span className={`w-8 text-right ${hasVerifiedIncome ? 'text-green-400' : hasDepositedIncome ? 'text-blue-400' : 'text-amber-400'}`}>
              {hasVerifiedIncome ? '+15' : hasDepositedIncome ? '+10' : '-5'}
            </span>
            <span className="text-slate-300">
              {hasVerifiedIncome ? 'Verified income (payroll)' : hasDepositedIncome ? 'Bank-deposited income' : 'Self-reported income'}
            </span>
          </div>
        ) : (
          <div className="flex items-start gap-2 text-xs">
            <span className={`w-8 text-right ${inferredVerified ? 'text-green-400' : inferredDeposited ? 'text-blue-400' : 'text-amber-400'}`}>
              {inferredVerified ? '+15' : inferredDeposited ? '+10' : '-5'}
            </span>
            <span className="text-slate-300">
              {inferredVerified ? 'Verified income (payroll)' : inferredDeposited ? 'Plaid bank deposits' : 'Self-reported income'}
            </span>
          </div>
        )}

        {/* CRS/Credit bureau - show from reasons OR inferred */}
        {(hasCRS || noCRS) ? (
          <div className="flex items-start gap-2 text-xs">
            <span className={`w-8 text-right ${hasCRS ? 'text-green-400' : 'text-slate-500'}`}>
              {hasCRS ? '+5' : '0'}
            </span>
            <span className="text-slate-300">
              {hasCRS ? 'Equifax data via CRS' : 'No credit bureau data'}
            </span>
          </div>
        ) : inferredCRS ? (
          <div className="flex items-start gap-2 text-xs">
            <span className="w-8 text-right text-green-400">+5</span>
            <span className="text-slate-300">Equifax data via CRS</span>
          </div>
        ) : null}

        {/* Data gaps penalty if present */}
        {hasDataGaps && (
          <div className="flex items-start gap-2 text-xs">
            <span className="w-8 text-right text-red-400">-5</span>
            <span className="text-slate-300">Data gaps detected</span>
          </div>
        )}

        {/* Low quality penalty if present */}
        {hasLowQuality && (
          <div className="flex items-start gap-2 text-xs">
            <span className="w-8 text-right text-red-400">-10</span>
            <span className="text-slate-300">High income variance</span>
          </div>
        )}
      </div>

      {/* Final score with visual bar */}
      <div className="pt-2 border-t border-slate-700">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-slate-300 font-medium">Total Score:</span>
          <span className={`font-bold text-sm ${
            score >= 70 ? 'text-green-400' :
            score >= 50 ? 'text-amber-400' :
            'text-red-400'
          }`}>
            {score}/100
          </span>
        </div>
        {/* Mini progress bar */}
        <div className="w-full bg-slate-600 rounded-full h-1.5 mb-1">
          <div
            className={`h-1.5 rounded-full ${
              score >= 70 ? 'bg-green-500' :
              score >= 50 ? 'bg-amber-500' :
              'bg-red-500'
            }`}
            style={{ width: `${score}%` }}
          />
        </div>
        <div className="text-xs text-slate-400">
          {score >= 70 ? 'HIGH — Strong data quality' :
           score >= 50 ? 'MEDIUM — Acceptable data quality' :
           'LOW — Limited data quality'}
        </div>
      </div>
    </div>
  );
}

/**
 * BUG-UI-061/BUG-UI-062: Income Basis Comparison Component
 * Shows both attested and deposited income values with variance explanation
 * NOTE: Dashboard always displays DEPOSITED income to lenders (BUG-UI-062)
 */
function IncomeBasisComparison({ incomeBasis }: {
  incomeBasis: IncomeBasisData
}) {
  const { income_sources, income_confidence, selection_reason } = incomeBasis;
  const attested = income_sources.attested;
  const deposited = income_sources.deposited_net;

  // Calculate variance
  const hasVariance = attested && deposited && attested !== deposited;
  const variancePercent = hasVariance
    ? Math.abs(((attested - deposited) / attested) * 100)
    : 0;
  const isSignificantVariance = variancePercent > 20;

  const formatCurrency = (value?: number) => {
    if (!value) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className={`p-4 rounded-lg border ${isSignificantVariance ? 'border-amber-500 bg-amber-900/20' : 'border-slate-600 bg-slate-700/50'}`}>
      <div className="flex items-center gap-2 mb-3">
        {isSignificantVariance ? (
          <AlertTriangle className="w-4 h-4 text-amber-400" />
        ) : (
          <Info className="w-4 h-4 text-blue-400" />
        )}
        <span className="text-sm font-medium text-white">Income Source Comparison</span>
        {/* BUG-UI-062: Always show DEPOSITED as what's displayed to lenders */}
        <Badge
          variant="outline"
          className="text-xs border-green-500 text-green-400"
        >
          Displaying: DEPOSITED
        </Badge>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Attested Income */}
        <div className="p-3 rounded-lg bg-slate-700">
          <div className="text-xs text-slate-400 mb-1">User-Reported (Attested)</div>
          <div className="text-lg font-semibold text-white">{formatCurrency(attested)}</div>
        </div>

        {/* Deposited Income - BUG-UI-062: Always highlighted as displayed value */}
        <div className="p-3 rounded-lg bg-green-900/30 ring-1 ring-green-500">
          <div className="text-xs text-slate-400 mb-1">Deposited (Calculated)</div>
          <div className="text-lg font-semibold text-white">{formatCurrency(deposited)}</div>
          <div className="text-xs text-green-400 mt-1">✓ Displayed on Dashboard</div>
        </div>
      </div>

      {/* Variance Warning */}
      {hasVariance && (
        <div className={`mt-3 p-2 rounded text-xs ${isSignificantVariance ? 'bg-amber-900/30 text-amber-300' : 'bg-slate-600 text-slate-300'}`}>
          <strong>Variance:</strong> {variancePercent.toFixed(0)}% difference between reported and deposited income.
          {selection_reason && (
            <span className="block mt-1 text-slate-400">{selection_reason}</span>
          )}
        </div>
      )}

      {/* Confidence indicator */}
      <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
        <span>Confidence:</span>
        <div className="flex-1 bg-slate-600 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full ${income_confidence >= 0.7 ? 'bg-green-500' : income_confidence >= 0.4 ? 'bg-amber-500' : 'bg-red-500'}`}
            style={{ width: `${income_confidence * 100}%` }}
          />
        </div>
        <span>{(income_confidence * 100).toFixed(0)}%</span>
      </div>
    </div>
  );
}

// Helper functions

/**
 * BUG-UI-036 FIX: Extract YYYY-MM month key from date sources for reliable comparison
 * Avoids timezone issues by extracting the raw month key string directly
 */
function extractMonthKey(asOf?: string, periodStart?: string): string | null {
  // First try period.start - already in YYYY-MM-DD or YYYY-MM format
  if (periodStart) {
    const match = periodStart.match(/^(\d{4}-\d{2})/);
    if (match) return match[1];
  }

  // Then try as_of - ISO timestamp, extract year-month
  if (asOf) {
    const date = new Date(asOf);
    if (!isNaN(date.getTime())) {
      // Use UTC to avoid timezone shifts
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      return `${year}-${month}`;
    }
  }

  return null;
}

/**
 * BUG-UI-036: Format month from month_key (YYYY-MM) to readable format
 * BUG-UI-071 FIX: Use 'short' month format to match formatAsOfDate() output
 * This ensures date mismatch comparison works correctly
 */
function formatMonthFromKey(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  if (!year || !month) return monthKey;
  const date = new Date(parseInt(year), parseInt(month) - 1);
  // BUG-UI-071: Changed from 'long' to 'short' to match formatAsOfDate() output
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Format value based on unit type
 * BUG-UI-019: VeraScore shows "44.0%" - scores should NOT have % sign
 */
function formatValue(value: number, unit: string): string {
  switch (unit) {
    case 'USD':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    case 'PERCENT':
      return `${value.toFixed(1)}%`;
    case 'RATIO':
      return value.toFixed(2);
    case 'COUNT':
      // BUG-UI-019: Scores are COUNT unit, should display as integer without % or $
      return Math.round(value).toString();
    case 'MONTHS':
      return `${value.toFixed(1)} months`;
    default:
      return value.toLocaleString();
  }
}

function formatPeriod(period: EvidenceContract['period']): string {
  if (period.type === 'AS_OF') {
    return `Point-in-time snapshot`;
  }
  // BUG-UI-036: Handle case where only start is provided (common for monthly metrics)
  if (period.start) {
    const startDate = new Date(period.start);
    if (!isNaN(startDate.getTime())) {
      const monthLabel = startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (period.end) {
        const endDate = new Date(period.end);
        if (!isNaN(endDate.getTime())) {
          const endLabel = endDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          return `${monthLabel} - ${endLabel}`;
        }
      }
      // Single month period - show just that month
      return monthLabel;
    }
  }
  return period.type.replace('_', ' ');
}

function getTrendIcon(trend?: 'UP' | 'DOWN' | 'STABLE') {
  switch (trend) {
    case 'UP': return TrendingUp;
    case 'DOWN': return TrendingDown;
    default: return Minus;
  }
}

function getTrendColor(trend?: 'UP' | 'DOWN' | 'STABLE'): string {
  switch (trend) {
    case 'UP': return 'text-green-600';
    case 'DOWN': return 'text-red-600';
    default: return 'text-slate-500';
  }
}

/**
 * BUG-EPIC61-003: Get trend label
 */
function getTrendLabel(trend?: 'UP' | 'DOWN' | 'STABLE'): string {
  switch (trend) {
    case 'UP': return 'Improving';
    case 'DOWN': return 'Worsening';
    default: return 'Stable';
  }
}

/**
 * Format the "As Of" date field
 * BUG-UI-001: "As Of" shows "Invalid Date"
 * BUG-UI-018: "As Of" month mismatch - shows wrong month
 *
 * Priority order:
 * 1. Use period.start if it's a valid YYYY-MM format (most reliable)
 * 2. Use as_of if it's a valid ISO date
 * 3. Fallback to 'N/A'
 */
function formatAsOfDate(asOf?: string, periodStart?: string): string {
  // First try period.start (YYYY-MM-DD format from month_key)
  if (periodStart) {
    // Handle YYYY-MM format (e.g., "2025-12-01" from month_key + "-01")
    const date = new Date(periodStart);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }
    // Handle YYYY-MM format directly
    const match = periodStart.match(/^(\d{4})-(\d{2})/);
    if (match) {
      const [, year, month] = match;
      const monthDate = new Date(parseInt(year), parseInt(month) - 1);
      return monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }
  }

  // Then try as_of (ISO timestamp)
  if (asOf) {
    const date = new Date(asOf);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }
  }

  return 'N/A';
}

/**
 * Get effective unit for display, with overrides for common mismatches
 * BUG-UI-035, BUG-UI-037: Factor scores and VeraScore show % sign
 *
 * Even if backend sends wrong unit, we force correct display based on metric type
 */
function getEffectiveUnit(
  metricKey: string,
  unit: 'USD' | 'PERCENT' | 'COUNT' | 'RATIO' | 'MONTHS'
): 'USD' | 'PERCENT' | 'COUNT' | 'RATIO' | 'MONTHS' {
  const lowerKey = metricKey?.toLowerCase() || '';

  // Factor scores and VeraScore are 0-100 scale scores, NOT percentages
  // Override any incorrect unit to COUNT
  if (lowerKey.includes('factor') || lowerKey.includes('verascore') || lowerKey.includes('_score')) {
    return 'COUNT';
  }

  // Credit score is a count (0-850 scale)
  if (lowerKey === 'credit_score') {
    return 'COUNT';
  }

  // DTI ratio is actually a percentage
  if (lowerKey === 'dti_ratio') {
    return 'PERCENT';
  }

  // Trust the passed unit for everything else
  return unit;
}

// ============================================================================
// EPIC 61: HELPER FUNCTIONS FOR 3-BLOCK TEMPLATE
// ============================================================================

/**
 * US61.2: Get interpretation badge for a metric value
 * Now uses centralized metric banding configuration
 */
function getInterpretation(metricKey: string, value: number): { label: string; color: string; icon: string } | null {
  return getMetricInterpretation(metricKey, value);
}

/**
 * US61.2: Get banding display for a metric
 * BUG-EPIC61-004: Show specific applicable range instead of full scale
 */
function getBanding(metricKey: string, value?: number): string | null {
  const fullBanding = getMetricBandingDisplay(metricKey);
  if (!fullBanding || value === undefined) return fullBanding;

  // Get interpretation to determine which range applies
  const interpretation = getInterpretation(metricKey, value);
  if (!interpretation) return fullBanding;

  // Extract the specific range from the full banding string
  const ranges = fullBanding.split('•').map(r => r.trim());
  const applicableRange = ranges.find(range =>
    range.toLowerCase().includes(interpretation.label.toLowerCase())
  );

  if (applicableRange) {
    return `${interpretation.icon} ${applicableRange}`;
  }

  return fullBanding;
}

/**
 * Get "What Was Used" content for a metric
 * Returns array of label/value/note items
 * BUG-EPIC61-002: Enhanced with metric-specific content for all metrics
 */
function getWhatWasUsedContent(
  evidence: EvidenceContract,
  metricKey: string,
  incomeBasis?: IncomeBasisData
): Array<{ label: string; value: string; note?: string }> | null {
  const lowerKey = metricKey?.toLowerCase() || '';
  const intermediate = evidence.intermediate_metrics as any;

  // DTI Factor - Show debt and income inputs
  // BUG-UI-095 FIX: Backend stores as 'minimum_payments_all_debt', not 'monthly_debt_payments'
  // Also look in factor_audit_dto.inputs and component_breakdown for values
  if (lowerKey.includes('dti') || lowerKey.includes('debt_to_income')) {
    // Try multiple sources for values
    const factorDto = intermediate?.factor_audit_dto;
    const dtoInputs = factorDto?.inputs || [];
    const componentBreakdown = intermediate?.component_breakdown || [];

    // Find Monthly Debt Payments from various sources
    const dtoDebt = dtoInputs.find((i: any) => i.name?.toLowerCase().includes('debt payment'))?.value;
    const compDebt = componentBreakdown.find((c: any) => c.name?.toLowerCase().includes('debt payment'))?.value;
    // BUG-UI-142: Use ?? instead of || to preserve legitimate zero values
    const monthlyDebt = intermediate?.monthly_debt_payments ?? intermediate?.minimum_payments_all_debt ?? dtoDebt ?? compDebt ?? 0;

    // Find Monthly Gross Income from various sources
    const dtoIncome = dtoInputs.find((i: any) => i.name?.toLowerCase().includes('gross income'))?.value;
    const compIncome = componentBreakdown.find((c: any) => c.name?.toLowerCase().includes('gross income'))?.value;
    // BUG-UI-142: Use ?? instead of || to preserve legitimate zero values
    const monthlyIncome = intermediate?.monthly_gross_income ?? dtoIncome ?? compIncome ?? 0;

    const annualIncome = intermediate?.yearly_gross_income ?? intermediate?.annual_gross_income ?? monthlyIncome * 12;
    const dtiRatio = intermediate?.dti_ratio ?? (monthlyIncome > 0 ? (monthlyDebt / monthlyIncome) * 100 : 0);

    return [
      {
        label: 'Monthly gross income',
        value: formatCurrency(monthlyIncome),
        note: `Annualized: ${formatCurrency(annualIncome)}`
      },
      {
        label: 'Monthly debt payments',
        value: formatCurrency(monthlyDebt),
        note: 'Minimum monthly obligations from all debt sources'
      },
      {
        label: 'DTI ratio',
        value: `${dtiRatio.toFixed(1)}%`,
        note: 'Debt payments ÷ Gross income'
      }
    ];
  }

  // Income - Show deposited vs attested comparison
  if (lowerKey.includes('income') && incomeBasis) {
    const deposited = incomeBasis.income_sources.deposited_net;
    const attested = incomeBasis.income_sources.attested;

    const items: Array<{ label: string; value: string; note?: string }> = [];

    if (deposited) {
      items.push({
        label: 'Deposited income (calculated)',
        value: formatCurrency(deposited),
        note: 'Based on bank deposits over 6 months'
      });
    }

    if (attested && Math.abs(attested - (deposited || 0)) > 1000) {
      items.push({
        label: 'User-reported income',
        value: formatCurrency(attested),
        note: `Variance: ${Math.abs(((attested - (deposited || 0)) / attested) * 100).toFixed(0)}%`
      });
    }

    return items.length > 0 ? items : null;
  }

  // Savings Factor - Show income, expenses, net savings
  // BUG-SAVINGS-001 FIX: Also look in factor_audit_dto.inputs and component_breakdown for values
  if (lowerKey.includes('savings') && lowerKey.includes('factor')) {
    // Try multiple sources for monthly gross income (similar to DTI fix)
    const factorDto = intermediate?.factor_audit_dto;
    const dtoInputs = factorDto?.inputs || [];
    const componentBreakdown = intermediate?.component_breakdown || [];

    // Find Monthly Gross Income from various sources
    const dtoIncome = dtoInputs.find((i: any) => i.name?.toLowerCase().includes('gross income'))?.value;
    const compIncome = componentBreakdown.find((c: any) => c.name?.toLowerCase().includes('gross income'))?.value;
    // BUG-UI-142: Use ?? instead of || to preserve legitimate zero values
    const monthlyIncome = intermediate?.monthly_gross_income ?? dtoIncome ?? compIncome ?? 0;

    // Find Net Savings from various sources
    const dtoNetSavings = dtoInputs.find((i: any) => i.name?.toLowerCase().includes('net saving'))?.value;
    const compNetSavings = componentBreakdown.find((c: any) => c.name?.toLowerCase().includes('net saving'))?.value;
    // BUG-UI-142: Use ?? instead of || to preserve legitimate zero values
    const netSavings = intermediate?.net_savings ?? dtoNetSavings ?? compNetSavings ?? 0;

    // Find Savings Rate from various sources
    const dtoDerived = factorDto?.derived || [];
    const dtoSavingsRate = dtoDerived.find((d: any) => d.name?.toLowerCase().includes('savings rate') || d.name?.toLowerCase().includes('savings pct'))?.value;
    const compSavingsRate = componentBreakdown.find((c: any) => c.name?.toLowerCase().includes('savings rate') || c.name?.toLowerCase().includes('savings pct'))?.value;
    // BUG-UI-142: Use ?? instead of || to preserve legitimate zero values
    const savingsRate = intermediate?.savings_pct ?? intermediate?.savings_rate ?? dtoSavingsRate ?? compSavingsRate ??
      (monthlyIncome > 0 ? (netSavings / monthlyIncome) * 100 : 0);

    return [
      {
        label: 'Monthly gross income',
        value: formatCurrency(monthlyIncome),
        note: 'Total income for the period'
      },
      {
        label: 'Net savings',
        value: formatCurrency(netSavings),
        note: 'Income minus expenses and debt payments'
      },
      {
        label: 'Savings rate',
        value: `${savingsRate.toFixed(1)}%`,
        note: 'Net savings ÷ Gross income'
      }
    ];
  }

  // Savings Rate (net_savings_1m) - Show calculation
  // BUG-SAVINGS-001 FIX: Also look in factor_audit_dto.inputs and component_breakdown for values
  if (lowerKey.includes('net_savings') || (lowerKey.includes('savings') && !lowerKey.includes('factor'))) {
    // Try multiple sources for monthly gross income (similar to DTI fix)
    const factorDto = intermediate?.factor_audit_dto;
    const dtoInputs = factorDto?.inputs || [];
    const componentBreakdown = intermediate?.component_breakdown || [];

    // Find Monthly Gross Income from various sources
    const dtoIncome = dtoInputs.find((i: any) => i.name?.toLowerCase().includes('gross income'))?.value;
    const compIncome = componentBreakdown.find((c: any) => c.name?.toLowerCase().includes('gross income'))?.value;
    // BUG-UI-142: Use ?? instead of || to preserve legitimate zero values; add money_in as fallback
    const monthlyIncome = intermediate?.monthly_gross_income ?? intermediate?.total_income ?? intermediate?.money_in ?? dtoIncome ?? compIncome ?? 0;

    // Find Net Savings from various sources
    const dtoNetSavings = dtoInputs.find((i: any) => i.name?.toLowerCase().includes('net saving'))?.value;
    const compNetSavings = componentBreakdown.find((c: any) => c.name?.toLowerCase().includes('net saving'))?.value;
    // BUG-UI-142: Use ?? instead of || to preserve legitimate zero values
    const netSavings = intermediate?.net_savings ?? intermediate?.total_savings ?? dtoNetSavings ?? compNetSavings ?? evidence.value ?? 0;

    const savingsRate = monthlyIncome > 0 ? (netSavings / monthlyIncome) * 100 : 0;

    return [
      {
        label: 'Net savings',
        value: formatCurrency(netSavings),
        note: 'Income minus all expenses and debt payments'
      },
      {
        label: 'Monthly gross income',
        value: formatCurrency(monthlyIncome),
        note: 'Total income for the period'
      },
      {
        label: 'Savings rate',
        value: `${savingsRate.toFixed(1)}%`,
        note: 'Percentage of income saved'
      }
    ];
  }

  // Liquid Reserves Factor - Show assets and expenses
  // BUG-EPIC61-012 FIX: Use correct backend field names for liquid reserves
  // Backend sends: liquid_reserves_balance, total_monthly_expenses, months_reserves
  if (lowerKey.includes('liquid') || lowerKey.includes('reserves')) {
    const liquidAssets = intermediate?.liquid_reserves_balance || intermediate?.total_liquid_assets || intermediate?.liquid_assets_median_30d || 0;
    const monthlyExpenses = intermediate?.total_monthly_expenses || intermediate?.monthly_expenses || 0;
    const reservesMonths = intermediate?.months_reserves || intermediate?.reserves_months || (monthlyExpenses > 0 ? liquidAssets / monthlyExpenses : 0);

    return [
      {
        label: 'Total liquid assets',
        value: formatCurrency(liquidAssets),
        note: 'Cash and easily accessible funds'
      },
      {
        label: 'Monthly expenses',
        value: formatCurrency(monthlyExpenses),
        note: 'Average monthly spending'
      },
      {
        label: 'Months of reserves',
        value: `${reservesMonths.toFixed(1)} months`,
        note: 'How long assets can cover expenses'
      }
    ];
  }

  // Cash Flow Factor - Show money in/out
  // BUG-UI-101 FIX: Also look in factor_audit_dto.inputs and component_breakdown for values
  if (lowerKey.includes('cash_flow') || lowerKey.includes('cashflow')) {
    // Try multiple sources for money_in/money_out values
    const factorDto = intermediate?.factor_audit_dto;
    const dtoInputs = factorDto?.inputs || [];
    const componentBreakdown = intermediate?.component_breakdown || [];

    // Find Money In from various sources
    const dtoMoneyIn = dtoInputs.find((i: any) => i.name?.toLowerCase() === 'money in')?.value;
    const compMoneyIn = componentBreakdown.find((c: any) => c.name?.toLowerCase() === 'money in')?.value;
    const moneyIn = intermediate?.money_in || dtoMoneyIn || compMoneyIn || 0;

    // Find Money Out from various sources
    const dtoMoneyOut = dtoInputs.find((i: any) => i.name?.toLowerCase() === 'money out')?.value;
    const compMoneyOut = componentBreakdown.find((c: any) => c.name?.toLowerCase() === 'money out')?.value;
    const moneyOut = intermediate?.money_out || dtoMoneyOut || compMoneyOut || 0;

    // Find Money Left Over / net flow
    const dtoDerived = factorDto?.derived || [];
    const dtoMoneyLeftOver = dtoDerived.find((d: any) => d.name?.toLowerCase().includes('money left'))?.value;
    const compMoneyLeftOver = componentBreakdown.find((c: any) => c.name?.toLowerCase().includes('money left'))?.value;
    const netFlow = intermediate?.net_flow || intermediate?.money_left_over || dtoMoneyLeftOver || compMoneyLeftOver || (moneyIn - moneyOut);

    const cashFlowPct = moneyIn > 0 ? (netFlow / moneyIn) * 100 : 0;

    return [
      {
        label: 'Money in',
        value: formatCurrency(moneyIn),
        note: 'Total income and deposits'
      },
      {
        label: 'Money out',
        value: formatCurrency(moneyOut),
        note: 'Total expenses and withdrawals'
      },
      {
        label: 'Net cash flow',
        value: formatCurrency(netFlow),
        note: `${cashFlowPct.toFixed(1)}% of income`
      }
    ];
  }

  // BUG-EPIC61-013 FIX: Debt Management Factor - Use correct backend field names
  // Backend sends: short_term_debt_balance, short_term_debt_payments, paid_ratio
  // NOTE: Backend does NOT send total_debt or total_credit_limit for this factor
  if (lowerKey.includes('debt') && (lowerKey.includes('management') || lowerKey.includes('factor'))) {
    const debtBalance = intermediate?.short_term_debt_balance || intermediate?.total_debt || 0;
    const debtPayments = intermediate?.short_term_debt_payments || 0;
    const paidRatio = intermediate?.paid_ratio || 0;

    return [
      {
        label: 'Short-term debt balance',
        value: formatCurrency(Math.abs(debtBalance)),
        note: 'Credit card and short-term debt'
      },
      {
        label: 'Debt payments made',
        value: formatCurrency(debtPayments),
        note: 'Payments toward short-term debt'
      },
      {
        label: 'Paid ratio',
        value: paidRatio === 999999 ? 'N/A' : `${paidRatio.toFixed(1)}%`,
        note: 'Payments ÷ Balance'
      }
    ];
  }

  // BUG-EPIC61-007: Total Debt (raw metric) - Show debt breakdown
  if (lowerKey.includes('total_debt') || lowerKey === 'outstanding_debt') {
    const totalDebt = evidence.value || 0;
    const creditLimit = intermediate?.total_credit_limit || 0;
    const utilization = creditLimit > 0 ? (totalDebt / creditLimit) * 100 : 0;

    const items: Array<{ label: string; value: string; note?: string }> = [
      {
        label: 'Total outstanding debt',
        value: formatCurrency(totalDebt),
        note: 'Sum of all debt obligations'
      }
    ];

    if (creditLimit > 0) {
      items.push({
        label: 'Total credit limit',
        value: formatCurrency(creditLimit),
        note: 'Available credit across all accounts'
      });
      items.push({
        label: 'Credit utilization',
        value: `${utilization.toFixed(1)}%`,
        note: 'Debt ÷ Credit limit'
      });
    }

    return items;
  }

  // Payment History Factor - Show late payments
  // BUG-UI-143 FIX: Use ?? (nullish coalescing) instead of || (logical OR)
  // When number_of_late_payments is 0 (perfect history), || treats it as falsy
  // and falls through to evidence.value (the factor SCORE, e.g. 100), displaying
  // "Late payments: 100" for a member with zero late payments.
  // NEVER fall back to evidence.value here — it's the factor score, not a count.
  if (lowerKey.includes('payment') && lowerKey.includes('history')) {
    const latePayments = intermediate?.number_of_late_payments ?? 0;
    const ageOfLatest = intermediate?.age_of_late_payments ?? 0;

    return [
      {
        label: 'Late payments (12 months)',
        value: `${latePayments}`,
        note: 'Number of late payments in the past year'
      },
      {
        label: 'Most recent late payment',
        value: ageOfLatest > 0 ? `${ageOfLatest} months ago` : 'None',
        note: 'Age of most recent late payment'
      }
    ];
  }

  // VeraScore Current - Show 6 factor scores as INPUTS (not the output)
  // FIX: Summary should show the INPUT factor scores, not the final VeraScore
  if (lowerKey.includes('verascore')) {
    // BUG-UI-135 FIX: Extract factor scores from intermediate_metrics
    // The backend stores these under DIFFERENT keys depending on whether the CURVE or LINEAR
    // scoring system is active (controlled by VERASCORE_CURVE_SCORING_ENABLED feature flag):
    //   CURVE:  stage1_debt_to_income_score, stage1_debt_management_score, stage1_payment_history_score
    //   LINEAR: debt_to_income_score, debt_management_score, payment_history_score
    // Cash flow, savings, and liquid_reserves use the same key names in both systems.
    // We check BOTH key patterns so the UI works regardless of which system is active.
    const dtiScore = intermediate?.stage1_debt_to_income_score ?? intermediate?.debt_to_income_score ?? 0;
    const liquidReservesScore = intermediate?.liquid_reserves_score ?? intermediate?.stage1_liquid_reserves_score ?? 0;
    const cashFlowScore = intermediate?.cash_flow_score ?? 0;
    const savingsScore = intermediate?.savings_score ?? 0;
    const debtManagementScore = intermediate?.stage1_debt_management_score ?? intermediate?.debt_management_score ?? 0;
    const paymentHistoryScore = intermediate?.stage1_payment_history_score ?? intermediate?.payment_history_score ?? 0;

    // MVP-082 FIX (Option A): Removed hardcoded weight percentages from note strings.
    // The old notes mixed Curve + Linear sub-weights summing to 86.5% instead of 100%.
    // Weights are correctly displayed in the Breakdown tab (2-stage grouped) and
    // Calculation tab (linear weights). See workplan I1.6 for full analysis.
    return [
      {
        label: 'DTI Factor Score',
        value: `${Math.round(dtiScore)}/100`,
        note: 'Debt-to-income ratio score'
      },
      {
        label: 'Liquid Reserves Score',
        value: `${Math.round(liquidReservesScore)}/100`,
        note: 'Emergency fund coverage score'
      },
      {
        label: 'Cash Flow Score',
        value: `${Math.round(cashFlowScore)}/100`,
        note: 'Income vs expenses ratio score'
      },
      {
        label: 'Savings Score',
        value: `${Math.round(savingsScore)}/100`,
        note: 'Regular savings activity score'
      },
      {
        label: 'Debt Management Score',
        value: `${Math.round(debtManagementScore)}/100`,
        note: 'Adverse debt handling score'
      },
      {
        label: 'Payment History Score',
        value: `${Math.round(paymentHistoryScore)}/100`,
        note: 'On-time payment track record'
      }
    ];
  }

  // BUG-EPIC61-010: Money In (money_in_1m) - Show deposit breakdown
  if (lowerKey === 'money_in_1m' || lowerKey === 'money_in') {
    const totalMoneyIn = evidence.value || 0;
    const checkingDeposits = intermediate?.checking_deposits || 0;
    const savingDeposits = intermediate?.saving_deposits || intermediate?.savings_deposits || 0;
    const investmentDeposits = intermediate?.investment_deposits || 0;
    const txnCount = intermediate?.txn_count || 0;

    const items: Array<{ label: string; value: string; note?: string }> = [
      {
        label: 'Total money in',
        value: formatCurrency(totalMoneyIn),
        note: 'All deposits for the month'
      }
    ];

    if (checkingDeposits > 0) {
      items.push({
        label: 'Checking deposits',
        value: formatCurrency(checkingDeposits),
        note: 'Deposits to checking accounts'
      });
    }

    if (savingDeposits > 0) {
      items.push({
        label: 'Savings deposits',
        value: formatCurrency(savingDeposits),
        note: 'Deposits to savings accounts'
      });
    }

    if (investmentDeposits > 0) {
      items.push({
        label: 'Investment deposits',
        value: formatCurrency(investmentDeposits),
        note: 'Deposits to investment accounts'
      });
    }

    if (txnCount > 0) {
      items.push({
        label: 'Transaction count',
        value: `${txnCount}`,
        note: 'Number of deposit transactions'
      });
    }

    return items;
  }

  // BUG-EPIC61-010: Money Out (money_out_1m) - Show withdrawal breakdown
  if (lowerKey === 'money_out_1m' || lowerKey === 'money_out') {
    const totalMoneyOut = evidence.value || 0;
    const checkingWithdrawals = intermediate?.checking_withdrawals || 0;
    const savingWithdrawals = intermediate?.saving_withdrawals || intermediate?.savings_withdrawals || 0;
    const investmentWithdrawals = intermediate?.investment_withdrawals || 0;
    const txnCount = intermediate?.txn_count || 0;

    const items: Array<{ label: string; value: string; note?: string }> = [
      {
        label: 'Total money out',
        value: formatCurrency(totalMoneyOut),
        note: 'All withdrawals and expenses for the month'
      }
    ];

    if (checkingWithdrawals > 0) {
      items.push({
        label: 'Checking withdrawals',
        value: formatCurrency(checkingWithdrawals),
        note: 'Withdrawals from checking accounts'
      });
    }

    if (savingWithdrawals > 0) {
      items.push({
        label: 'Savings withdrawals',
        value: formatCurrency(savingWithdrawals),
        note: 'Withdrawals from savings accounts'
      });
    }

    if (investmentWithdrawals > 0) {
      items.push({
        label: 'Investment withdrawals',
        value: formatCurrency(investmentWithdrawals),
        note: 'Withdrawals from investment accounts'
      });
    }

    if (txnCount > 0) {
      items.push({
        label: 'Transaction count',
        value: `${txnCount}`,
        note: 'Number of withdrawal transactions'
      });
    }

    return items;
  }

  // Default: show one-liner from summary
  if (evidence.summary?.one_liner) {
    return [
      {
        label: 'Calculation',
        value: evidence.summary.one_liner
      }
    ];
  }

  return null;
}

/**
 * Format currency helper
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * US61.3: Calculate confidence tier from confidence score
 * Epic 65M metadata
 */
function calculateConfidenceTier(confidenceScore?: number): 'HIGH' | 'MEDIUM' | 'LOW' {
  if (!confidenceScore) return 'LOW';
  if (confidenceScore >= 70) return 'HIGH';
  if (confidenceScore >= 50) return 'MEDIUM';
  return 'LOW';
}

/**
 * Get confidence color based on tier
 */
function getConfidenceColor(tier: 'HIGH' | 'MEDIUM' | 'LOW'): string {
  switch (tier) {
    case 'HIGH': return 'text-green-400';
    case 'MEDIUM': return 'text-amber-400';
    case 'LOW': return 'text-red-400';
  }
}

/**
 * Get confidence icon based on tier
 */
function getConfidenceIcon(tier: 'HIGH' | 'MEDIUM' | 'LOW'): React.ReactNode {
  switch (tier) {
    case 'HIGH': return <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />;
    case 'MEDIUM': return <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />;
    case 'LOW': return <AlertCircle className="w-4 h-4 text-red-400 mt-0.5" />;
  }
}

/**
 * Format coverage display
 * BUG-EPIC61-009: Updated to use EvidenceRunMetadata type
 */
function formatCoverageDisplay(metadata: EvidenceRunMetadata): string {
  const parts: string[] = [];

  // Accounts
  if (metadata.input_account_count && metadata.input_account_count > 0) {
    parts.push(`${metadata.input_account_count} account${metadata.input_account_count !== 1 ? 's' : ''}`);
  }

  // Months
  if (metadata.coverage_months_used && metadata.coverage_months_available) {
    parts.push(`${metadata.coverage_months_used}/${metadata.coverage_months_available} months`);
  } else if (metadata.input_months_coverage) {
    parts.push(`${metadata.input_months_coverage} months`);
  }

  // Transactions
  if (metadata.input_txn_count && metadata.input_txn_count > 0) {
    parts.push(`${metadata.input_txn_count.toLocaleString()} transactions`);
  }

  return parts.length > 0 ? parts.join(', ') : 'Coverage data not available';
}

/**
 * Format confidence reasons into human-readable text
 */
function formatConfidenceReasons(reasons: string[]): string {
  const reasonLabels: Record<string, string> = {
    'HIGH_COVERAGE': '6+ months of data',
    'PARTIAL_COVERAGE': '3-5 months of data',
    'LOW_COVERAGE': '<3 months of data',
    'VERIFIED_INCOME': 'Verified income source',
    'DEPOSITED_INCOME': 'Bank-deposited income',
    'ATTESTED_INCOME': 'Self-reported income',
    'CRS_AVAILABLE': 'Credit bureau data available',
    'CRS_UNAVAILABLE': 'No credit bureau data',
    'DATA_GAPS_DETECTED': 'Data gaps detected',
    'HIGH_QUALITY': 'High data quality',
    'MEDIUM_QUALITY': 'Medium data quality',
    'LOW_QUALITY': 'Low data quality',
    'HIGH_VARIANCE': 'High income variance',
  };

  return reasons
    .map(code => reasonLabels[code] || code)
    .join(' • ');
}

/**
 * Format basis label into human-readable text
 */
/**
 * BUG-UI-109 FIX: Improved labels for income/debt basis
 * BUG-UI-110 FIX: Show "Equifax via CRS" instead of generic "Credit Bureau"
 */
function formatBasisLabel(basis: string): string {
  const labels: Record<string, string> = {
    'VERIFIED_GROSS': 'Verified Gross (Payroll)',
    'DEPOSITED_NET': 'Plaid (Bank Deposits)',      // BUG-UI-109: Was "Deposited Net (Bank)"
    'ATTESTED': 'Self-Reported',
    'HYBRID': 'Hybrid (Multiple Sources)',
    'CRS_BUREAU': 'Equifax via CRS',               // BUG-UI-110: Was "Credit Bureau"
    'PLAID_LIABILITIES': 'Plaid Liabilities',
  };

  return labels[basis] || basis;
}
